import type { Session, User } from '@supabase/supabase-js';

import { supabase } from '@/shared/lib/supabase';

/**
 * Centralized Supabase Authentication Service
 * Single source of truth for all Supabase auth operations
 * Replaces the custom token management system
 */
class SupabaseAuthService {
  private static instance: SupabaseAuthService;
  private cachedToken: string | null = null;
  private tokenExpiry: number = 0;
  private pendingTokenRequest: Promise<string | null> | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): SupabaseAuthService {
    if (!SupabaseAuthService.instance) {
      SupabaseAuthService.instance = new SupabaseAuthService();
    }
    return SupabaseAuthService.instance;
  }

  /**
   * Get current session from Supabase
   * This includes access_token, refresh_token, and user data
   */
  public async getSession(): Promise<{ session: Session | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.getSession();
      return { session: data.session, error };
    } catch (error) {
      return { session: null, error: error as Error };
    }
  }

  /**
   * Get current user from Supabase
   */
  public async getUser(): Promise<{ user: User | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.getUser();
      return { user: data.user, error };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  }

  /**
   * Manually refresh the session
   * Supabase handles this automatically, but we can force it if needed
   */
  public async refreshSession(): Promise<{ session: Session | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return { session: data.session, error };
    } catch (error) {
      return { session: null, error: error as Error };
    }
  }

  /**
   * Sign out the user
   */
  public async signOut(): Promise<{ error: Error | null }> {
    try {
      // Clear cached token
      this.cachedToken = null;
      this.tokenExpiry = 0;
      this.pendingTokenRequest = null;

      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Listen to auth state changes
   * Returns unsubscribe function
   */
  public onAuthStateChange(callback: (event: string, session: Session | null) => void): () => void {
    const { data } = supabase.auth.onAuthStateChange(callback);
    return () => data.subscription.unsubscribe();
  }

  /**
   * Get access token from current session
   * Convenience method for API requests with caching
   */
  public async getAccessToken(): Promise<string | null> {
    try {
      // Check if we have a valid cached token
      const now = Date.now();
      if (this.cachedToken && this.tokenExpiry > now + 30000) {
        // 30 seconds buffer
        return this.cachedToken;
      }

      // If there's already a pending request, return it
      if (this.pendingTokenRequest) {
        return await this.pendingTokenRequest;
      }

      // Create a new request and cache it
      this.pendingTokenRequest = this.fetchFreshToken();
      const token = await this.pendingTokenRequest;
      this.pendingTokenRequest = null;

      return token;
    } catch {
      this.pendingTokenRequest = null;
      return null;
    }
  }

  private async fetchFreshToken(): Promise<string | null> {
    try {
      const { session } = await this.getSession();
      if (session?.access_token) {
        this.cachedToken = session.access_token;
        // Cache token for most of its lifetime (JWT tokens usually valid for 1 hour)
        this.tokenExpiry = (session.expires_at || 0) * 1000;
        return session.access_token;
      }
      this.cachedToken = null;
      this.tokenExpiry = 0;
      return null;
    } catch {
      this.cachedToken = null;
      this.tokenExpiry = 0;
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  public async isAuthenticated(): Promise<boolean> {
    const { session } = await this.getSession();
    return !!session;
  }
}

export const supabaseAuthService = SupabaseAuthService.getInstance();
